import React, { useState } from 'react';
import './PriceFilter.css';
import { AssetUrls } from 'config/assets';

interface PriceFilterProps {
  defaultMin?: number;
  defaultMax?: number;
  onChange?: (min: number, max: number) => void;
  /**
   * 'points' renders the single 34px field used by the printings header (one
   * coin-labelled field, both values inside). The default two-box layout is
   * what the Send toolbars still use.
   */
  variant?: 'default' | 'points';
}

const formatNumber = (value: number) => value.toLocaleString('en-US');

const PriceFilter: React.FC<PriceFilterProps> = ({
  defaultMin = 4353,
  defaultMax = 54999,
  onChange,
  variant = 'default',
}) => {
  const [minPrice, setMinPrice] = useState(defaultMin);
  const [maxPrice, setMaxPrice] = useState(defaultMax);

  const handleMinChange = (value: number) => {
    setMinPrice(value);
    onChange?.(value, maxPrice);
  };

  const handleMaxChange = (value: number) => {
    setMaxPrice(value);
    onChange?.(minPrice, value);
  };

  const parseInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    Number(e.target.value.replace(/,/g, '')) || 0;

  const calculateWidth = (value: number) => {
    const digits = value.toString().length;
    return `${Math.max(5, digits + 1)}ch`; // minimum width of 5ch
  };

  if (variant === 'points') {
    // The unit on this site is points, so the field says "Points" (with the
    // coin) and never "Price". Same onChange contract and stepper arithmetic
    // as the default layout; only the presentation differs. The steppers live
    // INSIDE the field and appear on hover/focus instead of being permanent
    // spinner columns.
    const stepper = (
      onUp: () => void,
      onDown: () => void,
      label: string,
    ) => (
      <span className="pf-points-steppers">
        <button type="button" aria-label={`Increase ${label}`} onClick={onUp}>
          <svg width="7" height="5" viewBox="0 0 7 5" fill="none" aria-hidden="true">
            <path d="M1 4L3.5 1.5L6 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button type="button" aria-label={`Decrease ${label}`} onClick={onDown}>
          <svg width="7" height="5" viewBox="0 0 7 5" fill="none" aria-hidden="true">
            <path d="M1 1L3.5 3.5L6 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </span>
    );

    return (
      <div className="pf-points-field">
        <img className="pf-points-coin" src={AssetUrls.icons.profile.coin} alt="" />
        <span className="pf-points-label">Points</span>
        <span className="pf-points-value">
          <input
            type="text"
            aria-label="Minimum points"
            style={{ width: `${minPrice.toString().length + 1}ch` }}
            value={formatNumber(minPrice)}
            onChange={(e) => handleMinChange(parseInput(e))}
          />
          {stepper(
            () => handleMinChange(minPrice + 1),
            () => handleMinChange(Math.max(0, minPrice - 1)),
            'minimum points',
          )}
        </span>
        <span className="pf-points-dash" aria-hidden="true" />
        <span className="pf-points-value">
          <input
            type="text"
            aria-label="Maximum points"
            style={{ width: `${maxPrice.toString().length + 1}ch` }}
            value={formatNumber(maxPrice)}
            onChange={(e) => handleMaxChange(parseInput(e))}
          />
          {stepper(
            () => handleMaxChange(maxPrice + 1),
            () => handleMaxChange(Math.max(minPrice, maxPrice - 1)),
            'maximum points',
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="price-filter">
      {/* MIN PRICE FIELD */}
      <div className="price-field">
        <label>Price (min.)</label>
        <div className="price-input-wrapper">
          <div className="price-arrows">
            <img
              src={AssetUrls.icons.dropdowns.arrowUp}
              alt="up"
              onClick={() => handleMinChange(minPrice + 1)}
            />
            <img
              src={AssetUrls.icons.dropdowns.arrowDown}
              alt="down"
              onClick={() => handleMinChange(Math.max(0, minPrice - 1))}
            />
          </div>
          <input
            type="text"
            style={{ width: calculateWidth(minPrice) }}
            value={formatNumber(minPrice)}
            onChange={(e) => handleMinChange(parseInput(e))}
          />
        </div>
      </div>

      {/* MAX PRICE FIELD */}
      <div className="price-field">
        <label>Price (max.)</label>
        <div className="price-input-wrapper">
          <div className="price-arrows">
            <img
              src={AssetUrls.icons.dropdowns.arrowUp}
              alt="up"
              onClick={() => handleMaxChange(maxPrice + 1)}
            />
            <img
              src={AssetUrls.icons.dropdowns.arrowDown}
              alt="down"
              onClick={() => handleMaxChange(Math.max(minPrice, maxPrice - 1))}
            />
          </div>
          <input
            type="text"
            style={{ width: calculateWidth(maxPrice) }}
            value={formatNumber(maxPrice)}
            onChange={(e) => handleMaxChange(parseInput(e))}
          />
        </div>
      </div>
    </div>
  );
};

export default PriceFilter;
